/* eslint-disable max-lines-per-function */
import * as React from 'react';
import moment from 'moment-timezone';
import './AssetThumbnailViewItem.scss';
import { ViewAssetDto, getThumbnailFilePathUsingFileName, isFolder } from '@deltasierra/shared';
import { useDoubleClick } from '@deltasierra/react-hooks';
import { AssetDisplayStyle } from '../../common';

export type AssetThumbnailViewItemProps = {
    asset: ViewAssetDto;
    containerDimensions: {
        height: number;
        width: number;
    };
    displayStyle?: AssetDisplayStyle;
    disabled?: boolean;
    onSingleClick?: (asset: ViewAssetDto) => void;
    onDoubleClick?: (asset: ViewAssetDto) => void;
    onClickTag?: (asset: ViewAssetDto) => void;
    onClickDownload?: (asset: ViewAssetDto) => void;
    onClickExpand: (asset: ViewAssetDto) => void;
};

// TODO: Refactor component and split up logic.

const AssetThumbnailViewItem: React.FunctionComponent<AssetThumbnailViewItemProps> = ({
    asset,
    containerDimensions,
    disabled = false,
    displayStyle = 'normal',
    onClickDownload,
    onClickExpand,
    onClickTag,
    onDoubleClick,
    onSingleClick,
}) => {
    // Any is used here due to issues importing from ViewAssetDto.TypeEnum.
    const getTimeDifferenceInMonths = (date: Date | undefined): number | undefined => {
        if (date) {
            const currentDate = moment();
            return Math.round(moment(date).diff(currentDate, 'months'));
        } else {
            return undefined;
        }
    };

    const getTimeDifferenceString = (date: Date | undefined) => {
        const timeDifferenceInMonths = getTimeDifferenceInMonths(date);
        if (timeDifferenceInMonths) {
            return `${timeDifferenceInMonths} ${timeDifferenceInMonths > 1 ? 'months' : 'month'}`;
        } else {
            return '-';
        }
    };

    const singleClick = React.useCallback(() => {
        if (onSingleClick && !disabled) {
            onSingleClick(asset);
        }
    }, [asset, disabled, onSingleClick]);

    const doubleClick = React.useCallback(() => {
        if (onDoubleClick && !disabled) {
            onDoubleClick(asset);
        }
    }, [asset, disabled, onDoubleClick]);

    const ref = useDoubleClick<HTMLDivElement>({
        delay: 200,
        onDoubleClick: doubleClick,
        onSingleClick: singleClick,
    });

    return (
        <div
            className={`asset-thumbnail-item ${displayStyle} ${disabled ? 'disabled' : ''}`}
            style={{
                width: containerDimensions.width,
            }}
        >
            <div
                className="asset-thumbnail-image-back"
                ref={ref}
                style={{
                    height: containerDimensions.height,
                    width: containerDimensions.width,
                }}
            >
                {isFolder(asset) && (
                    <div className="at-folder-thumbnail-icon" style={{ height: '80%' }}>
                        <div className="asset-thumbnail-fallback-title no-text-highlight">
                            <span title={asset.title}>{asset.title}</span>
                        </div>
                    </div>
                )}
                {!isFolder(asset) &&
                    asset.thumbnails.medium &&
                    containerDimensions.height > 0 &&
                    containerDimensions.width > 0 && (
                        <>
                            <div
                                className="asset-thumbnail-image-title-backdrop"
                                style={{ height: containerDimensions.height, width: containerDimensions.width }}
                            >
                                <div
                                    className="asset-thumbnail-image-title no-text-highlight"
                                    style={{ width: containerDimensions.width }}
                                >
                                    <span title={asset.title}>{asset.title}</span>
                                </div>
                            </div>
                            <img
                                alt={asset.title}
                                className="asset-thumbnail-image"
                                height={containerDimensions.height}
                                src={asset.thumbnails.medium.url}
                            />
                        </>
                    )}
                {!isFolder(asset) && !asset.thumbnails.medium && (
                    <div className="asset-thumbnail-fallback">
                        <div className="asset-thumbnail-fallback-title no-text-highlight">
                            <span title={asset.title}>{asset.title}</span>
                        </div>
                        <img
                            alt={asset.title}
                            className="asset-thumbnail-fallback-icon"
                            height={(containerDimensions.height * 1.8) / 3}
                            src={getThumbnailFilePathUsingFileName(asset.title)}
                        />
                        <div className="asset-thumbnail-fallback-size">{`${Math.round(asset.size / 1000)} kB`}</div>
                    </div>
                )}
            </div>
            <div
                className="asset-thumbnail-bottom-bar"
                style={{ backgroundColor: displayStyle === 'selected' ? '#6b30fa' : 'black' }}
            >
                {!isFolder(asset) && (
                    <>
                        <div>
                            {asset.expires && (
                                <>
                                    <i className="glyphicon glyphicon-time" />
                                    <span className="asset-thumbnail-expiry">
                                        {getTimeDifferenceString(asset.expires)}
                                    </span>
                                </>
                            )}
                        </div>
                        <span style={{ paddingLeft: 5 }}>
                            {onClickTag && (
                                <i
                                    className="asset-thumbnail-icon glyphicon glyphicon-tags text-primary"
                                    onClick={() => onClickTag(asset)}
                                />
                            )}
                            {onClickDownload && (
                                <i
                                    className="asset-thumbnail-icon glyphicon glyphicon-download-alt text-primary"
                                    onClick={() => onClickDownload(asset)}
                                />
                            )}
                            <i
                                className="asset-thumbnail-icon glyphicon glyphicon-search text-primary"
                                onClick={() => onClickExpand(asset)}
                            />
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};

AssetThumbnailViewItem.displayName = 'AssetThumbnailViewItem';

export default AssetThumbnailViewItem;
